import type React from 'react';
import { createContext, useContext, useId, useState } from 'react';

import { cn } from '../../lib/utils';
import { Switch } from '../switch';
import { Text } from '../text';

interface BoxContextValue {
  hasSwitch: boolean;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  titleId: string;
}

const BoxContext = createContext<BoxContextValue>({
  hasSwitch: false,
  isOpen: true,
  setOpen: () => true,
  titleId: '',
});

type BoxProps = React.ComponentProps<'div'> & {
  hasSwitch?: boolean;
  defaultOpen?: boolean;
};

function Box({
  className,
  hasSwitch = false,
  defaultOpen = false,
  ...props
}: BoxProps) {
  const [isOpen, setOpen] = useState(hasSwitch ? defaultOpen : true);
  const titleId = useId();

  return (
    <BoxContext.Provider value={{ hasSwitch, isOpen, setOpen, titleId }}>
      <div
        data-slot={'box'}
        className={cn(
          'bg-neutral border border-solid border-neutral-border rounded-xl overflow-hidden transition-all',
          className
        )}
        {...props}
      />
    </BoxContext.Provider>
  );
}

function BoxTitle({ className, ...props }: React.ComponentProps<typeof Text>) {
  const { hasSwitch, isOpen, setOpen, titleId } = useContext(BoxContext);

  return (
    <div
      data-slot={'box-title'}
      className={cn(
        'flex items-center justify-between p-5 transition-[padding-bottom]',
        !hasSwitch && 'last:pb-5 pb-0',
        hasSwitch && !isOpen && 'pb-5',
        hasSwitch && isOpen && 'pb-0'
      )}
    >
      <Text
        id={titleId}
        preset={'heading-sm'}
        className={cn(className)}
        {...props}
      />
      {hasSwitch && (
        <Switch
          size={'sm'}
          checked={isOpen}
          onCheckedChange={setOpen}
          aria-labelledby={titleId}
        />
      )}
    </div>
  );
}

function BoxContent({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  const { hasSwitch, isOpen } = useContext(BoxContext);

  if (!hasSwitch) {
    return (
      <div
        data-slot={'box-content'}
        className={cn('p-5', className)}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      data-slot={'box-content'}
      className={cn(
        'grid transition-[grid-template-rows]',
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        className
      )}
      {...props}
    >
      <div className={cn('overflow-hidden', !isOpen && 'invisible')}>
        <div className={'p-5'}>{children}</div>
      </div>
    </div>
  );
}

export { Box, BoxContent, BoxTitle };
